<template>
  <div class="patients-toolbar d-flex mb-5">
    <ToolbarSearchInput
      v-if="physician?.userId || permissionsService.hasRoleExternalUser"
      class="patients-toolbar__toolbar-search"
      :placeholder="$t('Find a patient')"
      v-model="searchInput"
      @update-search-input="updateSearchInput"
    />
    <div class="patients-toolbar__new-patient-btn">
      <Button
        v-if="showCreatePatientButton && hasPatientNewPermission"
        @click="createNewPatient()"
      >
        {{ $t("New Patient") }}
      </Button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { usePatientsStore } from "@/stores/patients";
import { usePhysiciansStore } from "@/stores/physicians";
import { usePermissionsService } from "@/composables/usePermissionsService";
import ToolbarSearchInput from "@/components/common/ToolbarSearchInput.vue";
import Button from "@/components/input-components/Button.vue";

defineOptions({ name: "PatientsToolbar" });

const router = useRouter();
const patientsStore = usePatientsStore();
const physiciansStore = usePhysiciansStore();
const permissionsService = usePermissionsService();

const searchInput = computed({
  get: () => patientsStore.searchInput ?? "",
  set: (value: string) => patientsStore.searchInput = value,
});

const physician = computed(() => physiciansStore.getPhysician);

const isSearchingByPhysician = computed(() => {
  const id = physician.value?.userId;
  return id !== "" && id !== null && id !== undefined;
});

const hasPatientReadPermission = computed(() =>
  permissionsService.hasPatientReadPermission
);

const hasPatientReadAllPermission = computed(() =>
  permissionsService.hasPatientReadAllPermission
);

const hasPatientCreatePermission = computed(() =>
  permissionsService.hasPatientCreatePermission
);

const hasPatientCreateAllPermission = computed(() =>
  permissionsService.hasPatientCreateAllPermission
);

const showCreatePatientButton = computed(() => {
  return (
    (hasPatientReadAllPermission.value && isSearchingByPhysician.value) ||
    (hasPatientReadPermission.value && !hasPatientReadAllPermission.value)
  );
});

const hasPatientNewPermission = computed(() => {
  return (
    hasPatientCreatePermission.value || hasPatientCreateAllPermission.value
  );
});

function updateSearchInput(value: string) {
  searchInput.value = value;
}

async function createNewPatient() {
  await patientsStore.resetPatient();
  router.push({ path: `/patient/new` });
}

onMounted(() => {
  patientsStore.searchInput = "";
});
</script>
<style lang="scss">
  .patients-toolbar {
    @media #{map.get($display-breakpoints, 'xs-only')} {
      flex-direction: column;

      .patients-toolbar__toolbar-search,
      .patients-toolbar__new-patient-btn {
        margin-bottom: 1rem;
      }
    }

    @media #{map.get($display-breakpoints, 'sm-and-up')} {
      .patients-toolbar__new-patient-btn {
        margin-left: auto;

        button {
          margin-left: 0.5rem;
        }
      }
    }
  }
</style>
